import React, { FC } from "react";
import {
  ChartLineUpIcon,
  EqualsIcon,
  TargetIcon,
  HashIcon,
  FunctionIcon,
  CubeIcon,
  HeartbeatIcon,
  LightningIcon,
  ArrowsLeftRightIcon,
  DropIcon,
} from "@phosphor-icons/react";
import style from "../ProfilingProtocolDetailPage.module.scss";

interface IMetricsRowProps {
  outputs: Record<string, any>;
}

/**
 * Mappa chiave output → label leggibile + unità + icona.
 * Estesa con campi Layout B "Indice" (sez.4.2):
 * VMA, VIFT, DSI, VO2max, HRmax, asymmetryPct, strongSide (spec sez.4.2).
 */
export const METRIC_META: Record<
  string,
  { label: string; unit: string; icon: React.ReactNode }
> = {
  // ── Layout A — regression / fv_jump / fv_sprint ──────────────────────────
  estimated1RM: {
    label: "1RM Stimato",
    unit: "kg",
    icon: <CubeIcon size={14} />,
  },
  slope: {
    label: "Slope",
    unit: "",
    icon: <ChartLineUpIcon size={14} />,
  },
  intercept: {
    label: "Intercept",
    unit: "m/s",
    icon: <FunctionIcon size={14} />,
  },
  R2: {
    label: "R²",
    unit: "",
    icon: <EqualsIcon size={14} />,
  },
  MVT: {
    label: "MVT",
    unit: "m/s",
    icon: <TargetIcon size={14} />,
  },
  n: {
    label: "N punti",
    unit: "",
    icon: <HashIcon size={14} />,
  },
  // ── Layout B — index (sez.4.2) ────────────────────────────────────────────
  VMA: {
    label: "VMA",
    unit: "km/h",
    icon: <LightningIcon size={14} />,
  },
  VIFT: {
    label: "VIFT",
    unit: "km/h",
    icon: <LightningIcon size={14} />,
  },
  DSI: {
    label: "DSI",
    unit: "",
    icon: <EqualsIcon size={14} />,
  },
  VO2max: {
    label: "VO₂max stimato",
    unit: "ml/kg/min",
    icon: <DropIcon size={14} />,
  },
  HRmax: {
    label: "FC max",
    unit: "bpm",
    icon: <HeartbeatIcon size={14} />,
  },
  asymmetryPct: {
    label: "Asimmetria",
    unit: "%",
    icon: <ArrowsLeftRightIcon size={14} />,
  },
};

function formatNumber(v: number): string {
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 100) return v.toFixed(1);
  return v.toFixed(2);
}

export const MetricsRow: FC<IMetricsRowProps> = ({ outputs }) => {
  const entries = Object.entries(outputs).filter(
    ([, v]) => typeof v === "number"
  );

  if (entries.length === 0) return null;

  return (
    <div className={style.metricsGrid}>
      {entries.map(([key, value]) => {
        const meta = METRIC_META[key];
        const label = meta?.label ?? key.toUpperCase();
        const unit = meta?.unit ?? "";
        const icon = meta?.icon ?? null;

        return (
          <div key={key} className={style.metricCard}>
            {icon && (
              <span className={style.metricCardIcon}>{icon}</span>
            )}
            <p className={style.metricLabel}>{label}</p>
            <p className={style.metricValue}>{formatNumber(value as number)}</p>
            {unit && <p className={style.metricUnit}>{unit}</p>}
          </div>
        );
      })}
    </div>
  );
};
